import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME, PageHeader, LOGO_URL } from "@/components/common"; // eslint-disable-line no-unused-vars
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, Trash2, Printer, Plus, Search, Receipt as ReceiptIcon, Ban } from "lucide-react";

const rp = (n) => "Rp " + new Intl.NumberFormat("id-ID").format(n || 0);
const PONDOK_ALAMAT = "Pondok Pesantren Mamba'us Sholihin · Suci - Gresik";

export default function KasirTransaksi() {
  const qc = useQueryClient();
  const { user, hasPerm } = useAuth();
  const { data: katalog = [] } = useQuery({ queryKey: ["katalog", "aktif"], queryFn: async () => (await api.get("/katalog")).data });

  const [pembeliTipe, setPembeliTipe] = useState("umum");
  const [pembeliNama, setPembeliNama] = useState("");
  const [santriQ, setSantriQ] = useState("");
  const [santri, setSantri] = useState(null);
  const [kelas, setKelas] = useState("");
  const [ribath, setRibath] = useState("");
  const [cart, setCart] = useState([]);
  const [selKat, setSelKat] = useState("");
  const [selUk, setSelUk] = useState("");
  const [qty, setQty] = useState(1);
  const [strukWidth, setStrukWidth] = useState("58");
  const [lastSale, setLastSale] = useState(null);
  const [diskon, setDiskon] = useState("");
  const [bayar, setBayar] = useState("");
  const [metode, setMetode] = useState("tunai");

  const { data: santriRes } = useQuery({
    queryKey: ["kasir-santri", santriQ], enabled: pembeliTipe === "santri" && santriQ.trim().length >= 2,
    queryFn: async () => (await api.get("/santri", { params: { page: 1, limit: 8, q: santriQ } })).data,
  });
  const santriList = santriRes?.items || [];

  const { data: history = [] } = useQuery({ queryKey: ["penjualan", "mine"], queryFn: async () => (await api.get("/penjualan")).data });
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const ribathOpts = useMemo(() => Array.from(new Set([...(options.ribath || []), ...history.map((h) => h.ribath).filter(Boolean)])), [options, history]);
  const kelasOpts = useMemo(() => Array.from(new Set([...(options.smt || []).map(String), ...history.map((h) => h.kelas).filter(Boolean)])), [options, history]);

  const curKat = katalog.find((k) => k.id === selKat);
  const subtotal = useMemo(() => cart.reduce((a, c) => a + c.harga * c.qty, 0), [cart]);
  const diskonPct = Math.max(0, Math.min(100, Number(diskon) || 0));
  const potongan = Math.round(subtotal * diskonPct / 100);
  const netTotal = Math.max(0, subtotal - potongan);
  const bayarN = bayar === "" ? null : Math.max(0, Number(bayar) || 0);
  const kembalian = bayarN == null ? null : bayarN - netTotal;

  const addToCart = () => {
    if (!curKat || !selUk) { toast.error("Pilih produk & ukuran"); return; }
    const q = Math.max(1, Number(qty) || 1);
    setCart((c) => {
      const idx = c.findIndex((x) => x.katalog_id === curKat.id && x.ukuran === selUk);
      if (idx >= 0) { const n = [...c]; n[idx] = { ...n[idx], qty: n[idx].qty + q }; return n; }
      return [...c, { katalog_id: curKat.id, nama: curKat.nama, ukuran: selUk, harga: curKat.harga, qty: q }];
    });
    setSelUk(""); setQty(1);
  };
  const rmCart = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const save = useMutation({
    mutationFn: async () => (await api.post("/penjualan", {
      pembeli_tipe: pembeliTipe, santri_id: pembeliTipe === "santri" ? santri?.id : null,
      pembeli_nama: pembeliTipe === "santri" ? (santri?.nama || "") : pembeliNama,
      kelas: kelas || null, ribath: ribath || null, metode_bayar: metode,
      diskon: diskonPct, bayar: bayarN,
      items: cart.map((c) => ({ katalog_id: c.katalog_id, ukuran: c.ukuran, qty: c.qty })),
    })).data,
    onSuccess: (d) => {
      setLastSale(d); toast.success(`Transaksi ${d.no_pembelian_fmt} tersimpan (${rp(d.total)})`);
      setCart([]); setPembeliNama(""); setSantri(null); setSantriQ(""); setDiskon(""); setBayar(""); setKelas(""); setRibath(""); setMetode("tunai");
      qc.invalidateQueries({ queryKey: ["penjualan"] }); qc.invalidateQueries({ queryKey: ["katalog"] }); qc.invalidateQueries({ queryKey: ["kas"] });
      setTimeout(() => window.print(), 500);
    },
    onError: (e) => toast.error(errMsg(e, "Gagal menyimpan transaksi")),
  });

  const voidMut = useMutation({
    mutationFn: async (id) => api.post(`/penjualan/${id}/void`),
    onSuccess: () => { toast.success("Transaksi dibatalkan, stok dikembalikan"); qc.invalidateQueries({ queryKey: ["penjualan"] }); qc.invalidateQueries({ queryKey: ["kas"] }); qc.invalidateQueries({ queryKey: ["katalog"] }); },
    onError: (e) => toast.error(errMsg(e, "Gagal membatalkan")),
  });

  const doPrint = () => window.print();
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/penjualan/${id}`),
    onSuccess: () => { toast.success("Transaksi dihapus — pemasukan Buku Kas disesuaikan"); qc.invalidateQueries({ queryKey: ["penjualan"] }); qc.invalidateQueries({ queryKey: ["kas"] }); qc.invalidateQueries({ queryKey: ["katalog"] }); },
    onError: (e) => toast.error(errMsg(e, "Gagal menghapus")),
  });
  const delAllMut = useMutation({
    mutationFn: async () => (await api.delete("/penjualan")).data,
    onSuccess: (r) => { toast.success(`${r.dihapus} transaksi dihapus`); qc.invalidateQueries({ queryKey: ["penjualan"] }); qc.invalidateQueries({ queryKey: ["kas"] }); qc.invalidateQueries({ queryKey: ["katalog"] }); },
    onError: (e) => toast.error(errMsg(e, "Gagal menghapus")),
  });

  return (
    <div className="space-y-5" data-testid="kasir-transaksi">
      <PageHeader icon={ShoppingCart} title="Transaksi Kasir" subtitle={<>Kasir: <strong>{user?.name}</strong> · No. Pembelian, tanggal & nama kasir terisi otomatis saat disimpan.</>} />

      <div className="grid gap-5 lg:grid-cols-5">
        {/* Left: input */}
        <div className="lg:col-span-3 space-y-4">
          <div className="glass rounded-2xl p-5 space-y-3">
            <Label className="text-xs">Pembeli</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={pembeliTipe === "umum" ? "default" : "outline"} className={`rounded-xl ${pembeliTipe === "umum" ? "bg-emerald-700" : "bg-white/60 dark:bg-white/5"}`} data-testid="pembeli-umum-btn" onClick={() => setPembeliTipe("umum")}>Umum / Non-santri</Button>
              <Button size="sm" variant={pembeliTipe === "santri" ? "default" : "outline"} className={`rounded-xl ${pembeliTipe === "santri" ? "bg-emerald-700" : "bg-white/60 dark:bg-white/5"}`} data-testid="pembeli-santri-btn" onClick={() => setPembeliTipe("santri")}>Santri Terdaftar</Button>
            </div>
            {pembeliTipe === "umum" ? (
              <Input placeholder="Nama pembeli (opsional)" value={pembeliNama} data-testid="pembeli-nama-input" onChange={(e) => setPembeliNama(e.target.value)} />
            ) : (
              <div className="space-y-2">
                {santri ? (
                  <div className="flex items-center justify-between bg-white/60 dark:bg-white/5 rounded-xl px-3 py-2" data-testid="pembeli-santri-selected">
                    <span className="text-sm font-medium text-emerald-950 dark:text-emerald-50">{santri.nama} <span className="text-slate-400">· {santri.no_induk_pondok}</span></span>
                    <Button variant="ghost" size="sm" onClick={() => setSantri(null)}>Ganti</Button>
                  </div>
                ) : (
                  <>
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                      <Input placeholder="Cari nama / No. Induk (min. 2 huruf)" value={santriQ} data-testid="santri-search-input" onChange={(e) => setSantriQ(e.target.value)} className="pl-9" /></div>
                    {santriList.length > 0 && (
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {santriList.map((s) => (
                          <button key={s.id} data-testid={`santri-pick-${s.no_induk_pondok}`} onClick={() => { setSantri(s); setSantriQ(""); }} className="w-full text-left text-sm bg-white/50 dark:bg-white/5 hover:bg-emerald-50 rounded-lg px-3 py-2">{s.nama} <span className="text-slate-400">· {s.no_induk_pondok}</span></button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Kelas (opsional)</Label>
                <Input placeholder="Pilih / ketik — mis. 7A" value={kelas} list="kasir-kelas-options" data-testid="kasir-kelas-input" onChange={(e) => setKelas(e.target.value)} />
                <datalist id="kasir-kelas-options">{kelasOpts.map((k) => <option key={k} value={k} />)}</datalist></div>
              <div className="space-y-1.5"><Label className="text-xs">Ribath (opsional)</Label>
                <Input placeholder="Pilih / ketik ribath" value={ribath} list="kasir-ribath-options" data-testid="kasir-ribath-input" onChange={(e) => setRibath(e.target.value)} />
                <datalist id="kasir-ribath-options">{ribathOpts.map((r) => <option key={r} value={r} />)}</datalist></div>
            </div>
          </div>

          <div className="glass rounded-2xl p-5 space-y-3">
            <Label className="text-xs">Tambah Item</Label>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
              <div className="sm:col-span-6 space-y-1"><span className="text-[11px] text-slate-500 dark:text-slate-300">Produk</span>
                <Select value={selKat} onValueChange={(v) => { setSelKat(v); setSelUk(""); }}>
                  <SelectTrigger data-testid="kasir-produk-select"><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                  <SelectContent>{katalog.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama} — {rp(k.harga)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3 space-y-1"><span className="text-[11px] text-slate-500 dark:text-slate-300">Ukuran</span>
                <Select value={selUk} onValueChange={setSelUk} disabled={!curKat}>
                  <SelectTrigger data-testid="kasir-ukuran-select"><SelectValue placeholder="Ukuran" /></SelectTrigger>
                  <SelectContent>{(curKat?.ukuran || []).map((u) => <SelectItem key={u.ukuran} value={u.ukuran}>{u.ukuran}{u.stok != null ? ` (stok ${u.stok})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3 flex gap-2">
                <Input type="number" min={1} value={qty} data-testid="kasir-qty-input" onChange={(e) => setQty(e.target.value)} className="w-full" />
                <Button className="rounded-xl bg-emerald-700 hover:bg-emerald-800 shrink-0" data-testid="kasir-add-item-btn" onClick={addToCart}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: cart */}
        <div className="lg:col-span-2 space-y-3">
          <div className="glass rounded-2xl p-5 space-y-3" data-testid="kasir-cart">
            <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">Keranjang ({cart.length})</h4>
            {cart.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Belum ada item.</p> : (
              <div className="space-y-2">
                {cart.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 bg-white/60 dark:bg-white/5 rounded-xl px-3 py-2" data-testid={`cart-item-${i}`}>
                    <div className="min-w-0"><p className="text-sm font-medium text-emerald-950 dark:text-emerald-50 truncate flex items-center gap-1">{c.nama} <Badge className="border-0 bg-emerald-100 text-emerald-700">{c.ukuran}</Badge></p>
                      <p className="text-xs text-slate-500 dark:text-slate-300">{c.qty} × {rp(c.harga)} = <strong>{rp(c.harga * c.qty)}</strong></p></div>
                    <Button variant="ghost" size="icon" onClick={() => rmCart(i)} data-testid={`cart-rm-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2 border-t border-white/60 dark:border-white/10 pt-3">
              <div className="flex items-center justify-between text-sm"><span className="text-slate-500 dark:text-slate-300">Subtotal</span><span className="font-medium text-emerald-900 dark:text-emerald-100">{rp(subtotal)}</span></div>
              <div className="flex items-center justify-between gap-2"><span className="text-sm text-slate-500 dark:text-slate-300 shrink-0">Diskon (%)</span>
                <Input type="number" min={0} max={100} value={diskon} data-testid="kasir-diskon-input" onChange={(e) => setDiskon(e.target.value)} placeholder="0" className="h-8 w-24 text-right" /></div>
              {diskonPct > 0 && <div className="flex items-center justify-between text-xs text-slate-400"><span>Potongan {diskonPct}%</span><span>-{rp(potongan)}</span></div>}
              <div className="flex items-center justify-between"><span className="text-sm text-slate-500 dark:text-slate-300">Total</span><span className="text-2xl font-extrabold text-emerald-800 dark:text-emerald-300" data-testid="kasir-total">{rp(netTotal)}</span></div>
              <div className="flex items-center justify-between gap-2"><span className="text-sm text-slate-500 dark:text-slate-300 shrink-0">Metode Bayar</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant={metode === "tunai" ? "default" : "outline"} className={`rounded-lg h-8 px-3 ${metode === "tunai" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-white/60 dark:bg-white/5"}`} data-testid="metode-tunai-btn" onClick={() => setMetode("tunai")}>Tunai</Button>
                  <Button size="sm" variant={metode === "transfer" ? "default" : "outline"} className={`rounded-lg h-8 px-3 ${metode === "transfer" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-white/60 dark:bg-white/5"}`} data-testid="metode-transfer-btn" onClick={() => setMetode("transfer")}>Transfer</Button>
                </div></div>
              <div className="flex items-center justify-between gap-2"><span className="text-sm text-slate-500 dark:text-slate-300 shrink-0">Uang Dibayar</span>
                <Input type="number" min={0} value={bayar} data-testid="kasir-bayar-input" onChange={(e) => setBayar(e.target.value)} placeholder="opsional" className="h-8 w-32 text-right" /></div>
              {kembalian != null && (
                <div className="flex items-center justify-between text-sm" data-testid="kasir-kembalian"><span className="text-slate-500 dark:text-slate-300">Kembalian</span>
                  <span className={`font-bold ${kembalian < 0 ? "text-rose-600" : "text-emerald-700"}`}>{rp(kembalian)}{kembalian < 0 ? " (kurang)" : ""}</span></div>
              )}
            </div>
            <Button className="w-full h-12 rounded-xl bg-emerald-700 hover:bg-emerald-800" data-testid="kasir-save-btn" disabled={save.isPending || cart.length === 0} onClick={() => save.mutate()}>Simpan Transaksi</Button>
          </div>

          {lastSale && (
            <div className="glass rounded-2xl p-5 space-y-3" data-testid="kasir-last-sale">
              <p className="text-sm text-emerald-800 dark:text-emerald-300 flex items-center gap-2"><ReceiptIcon className="h-4 w-4" /> Transaksi <strong>{lastSale.no_pembelian_fmt}</strong> tersimpan.</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-300">Lebar kertas:</span>
                <Select value={strukWidth} onValueChange={setStrukWidth}><SelectTrigger className="w-24 h-8" data-testid="struk-width-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="58">58mm</SelectItem><SelectItem value="80">80mm</SelectItem></SelectContent></Select>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 rounded-xl bg-emerald-700 hover:bg-emerald-800 gap-2" data-testid="cetak-struk-btn" onClick={doPrint}><Printer className="h-4 w-4" /> Cetak Struk</Button>
                <Button variant="outline" className="flex-1 rounded-xl bg-white/60 dark:bg-white/5 gap-2" data-testid="simpan-pdf-btn" onClick={doPrint}>Simpan PDF</Button>
              </div>
              <p className="text-[11px] text-slate-400">Klik "Cetak Struk" untuk membuka dialog print browser (pilih printer thermal). Untuk PDF, pilih tujuan "Simpan sebagai PDF" di dialog yang sama.</p>
            </div>
          )}
        </div>
      </div>

      {/* Riwayat */}
      <div className="glass rounded-2xl p-5" data-testid="kasir-history">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-emerald-900 dark:text-emerald-100">Riwayat Transaksi Saya ({history.length})</h4>
          {user?.role === "super_admin" && history.length > 0 && <Button variant="outline" size="sm" data-testid="hapus-semua-transaksi" className="text-xs text-rose-600 border-rose-200 hover:bg-rose-50 gap-1" onClick={() => { if (window.confirm("HAPUS SEMUA transaksi penjualan? Semua entri pemasukan penjualan di Buku Kas ikut terhapus & stok dikembalikan. Gunakan hanya untuk perbaikan kesalahan input. Tindakan permanen.")) delAllMut.mutate(); }}><Trash2 className="h-3.5 w-3.5" /> Hapus Semua</Button>}
        </div>
        {history.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Belum ada transaksi.</p> : (
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {history.map((h) => (
              <div key={h.id} className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm ${h.voided ? "bg-rose-50/70 dark:bg-rose-950/30" : "bg-white/50 dark:bg-white/5"}`} data-testid={`history-${h.no_pembelian_fmt}`}>
                <div className="min-w-0"><div className="font-medium text-emerald-950 dark:text-emerald-50 truncate">{h.no_pembelian_fmt} · {h.pembeli_nama} {h.voided && <Badge className="border-0 bg-rose-100 text-rose-700 ml-1">DIBATALKAN</Badge>}</div>
                  <p className="text-xs text-slate-400 truncate">{new Date(h.tanggal).toLocaleString("id-ID")} · {h.items.length} item</p></div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`font-bold ${h.voided ? "text-slate-400 line-through" : "text-emerald-700"}`}>{rp(h.total)}</span>
                  {!h.voided && <Button variant="ghost" size="icon" data-testid={`reprint-${h.no_pembelian_fmt}`} onClick={() => setLastSale(h)}><Printer className="h-4 w-4" /></Button>}
                  {!h.voided && hasPerm("kas.manage") && false && <span />}
                  {!h.voided && user?.role === "super_admin" && <Button variant="ghost" size="icon" data-testid={`void-${h.no_pembelian_fmt}`} onClick={() => { if (window.confirm(`Batalkan (void) transaksi ${h.no_pembelian_fmt}? Transaksi tetap tampil (ditandai "Dibatalkan"), stok dikembalikan, dan entri koreksi penyeimbang dibuat otomatis di Buku Kas. Hanya Super Admin.`)) voidMut.mutate(h.id); }}><Ban className="h-4 w-4 text-rose-500" /></Button>}
                  {user?.role === "super_admin" && <Button variant="ghost" size="icon" data-testid={`del-${h.no_pembelian_fmt}`} onClick={() => { if (window.confirm(`Hapus PERMANEN transaksi ${h.no_pembelian_fmt}? Pemasukan di Buku Kas berkurang otomatis & stok dikembalikan.`)) delMut.mutate(h.id); }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Struk cetak (hanya tampil saat print) */}
      {lastSale && <Receipt sale={lastSale} width={strukWidth} />}
    </div>
  );
}

function Receipt({ sale, width }) {
  return (
    <div id="struk-print" className={width === "80" ? "struk-80" : "struk-58"} style={{ fontFamily: "monospace", color: "#000", fontSize: width === "80" ? "12px" : "10px", lineHeight: 1.35, padding: "2mm" }}>
      <img src={LOGO_URL} alt="Logo" crossOrigin="anonymous" style={{ height: width === "80" ? "52px" : "42px", display: "block", margin: "0 auto 2px" }} />
      <div style={{ textAlign: "center", fontWeight: 700 }}>Kasir OSPMS</div>
      <div style={{ textAlign: "center", fontSize: "0.9em" }}>Pondok Pesantren Mamba'us Sholihin</div>
      <div style={{ textAlign: "center", fontSize: "0.8em" }}>{PONDOK_ALAMAT}</div>
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      <div>No : {sale.no_pembelian_fmt}</div>
      <div>Tgl: {new Date(sale.tanggal).toLocaleString("id-ID")}</div>
      <div>Kasir: {sale.kasir_nama}</div>
      <div>Pembeli: {sale.pembeli_nama}</div>
      {(sale.kelas || sale.ribath) && <div>Kelas/Ribath: {[sale.kelas, sale.ribath].filter(Boolean).join(" · ")}</div>}
      <div>Metode: {sale.metode_bayar === "transfer" ? "Transfer" : "Tunai"}</div>
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      {sale.items.map((i, idx) => (
        <div key={idx} style={{ marginBottom: 2 }}>
          <div>{i.nama} ({i.ukuran})</div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>{i.qty} x {new Intl.NumberFormat("id-ID").format(i.harga)}</span>
            <span>{new Intl.NumberFormat("id-ID").format(i.subtotal)}</span>
          </div>
        </div>
      ))}
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      {sale.diskon > 0 && (<>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{new Intl.NumberFormat("id-ID").format(sale.subtotal || sale.total)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Diskon{sale.diskon_persen ? ` (${sale.diskon_persen}%)` : ""}</span><span>-{new Intl.NumberFormat("id-ID").format(sale.diskon)}</span></div>
      </>)}
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.1em" }}>
        <span>TOTAL</span><span>{rp(sale.total)}</span>
      </div>
      {sale.bayar != null && (<>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Bayar</span><span>{new Intl.NumberFormat("id-ID").format(sale.bayar)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>Kembali</span><span>{new Intl.NumberFormat("id-ID").format(sale.kembalian || 0)}</span></div>
      </>)}
      <div style={{ borderTop: "1px dashed #000", margin: "4px 0" }} />
      <div style={{ textAlign: "center", fontSize: "0.82em", lineHeight: 1.4 }}>Terima kasih banyak sudah berbelanja di toko kami! Mohon pastikan kembali pesanan Anda, karena barang yang sudah dibeli tidak dapat ditukar atau dikembalikan.</div>
    </div>
  );
}
